import * as React from "react"
import { useTranslation } from "react-i18next";

import { cn } from "../../lib/utils"

export type AccountMenuItem = {
  to?: string
  label: React.ReactNode
  icon?: React.ReactNode
  onClick?: () => void
  active?: boolean
}

// 26BET design-kit AccountMenu: a gold pill trigger + a dark dropdown panel —
// see checklist item "Replace the hand-rolled account dropdown (plain divs)
// with a proper AccountMenu component".
function AccountMenu({
  label,
  items,
  open,
  onOpenChange,
  className,
}: {
  label?: React.ReactNode
  items: AccountMenuItem[]
  open: boolean
  onOpenChange: (open: boolean) => void
  className?: string
}) {
  const ref = React.useRef<HTMLDivElement>(null)
  const { t } = useTranslation()
  const resolvedLabel = label ?? t("account_menu_my_account", "My Account")

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onOpenChange(false)
      }
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [onOpenChange])

  return (
    <div className={cn("relative inline-block", className)} ref={ref}>
      <button
        type="button"
        data-slot="account-menu-trigger"
        onClick={() => onOpenChange(!open)}
        className="hidden w-fit items-center gap-1 !rounded-full bg-[var(--gold-500)] px-3 py-1.5 text-sm font-semibold text-[var(--on-brand)] hover:bg-[var(--gold-400)] md:flex"
      >
        <span className="truncate">{resolvedLabel}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className={cn("transition-transform", open && "rotate-180")}>
          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open ? (
        <div
          data-slot="account-menu-panel"
          className="absolute right-0 z-50 mt-2 w-48 rounded-[var(--radius-md)] !border !border-[var(--border-subtle)] bg-[var(--surface-card)] py-1 text-left text-sm shadow-lg overflow-hidden"
        >
          {items.map((item, i) => {
            const content = (
              <>
                {item.icon}
                <span className="bet-rail__label truncate">{item.label}</span>
              </>
            )
            const itemClass = "bet-rail__item no-underline !h-10 !px-4 !gap-3 text-sm"

            return item.to ? (
              <a
                key={i}
                href={item.to}
                onClick={item.onClick}
                className={itemClass}
                aria-current={item.active ? "true" : undefined}
              >
                {content}
              </a>
            ) : (
              <div
                key={i}
                onClick={item.onClick}
                className={cn(itemClass, "cursor-pointer")}
                aria-current={item.active ? "true" : undefined}
              >
                {content}
              </div>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

export { AccountMenu }
